import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from './AuthContext'; // Import useAuth hook

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: contextLogin, isAuthenticated, loading } = useAuth(); // Use context login
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Redirect authenticated users based on role
  useEffect(() => {
    if (isAuthenticated && !loading) {
      // Get user data from auth context
      const userData = JSON.parse(sessionStorage.getItem('user_data') || '{}');
      
      // Check if user is super admin
      if (userData.role === 'admin' || userData.role === 'admin') {
        navigate('/dashboard-admin', { replace: true });
      } else {
        const redirectTo = location.state?.from || '/dashboard';
        navigate(redirectTo, { replace: true });
      }
    }
  }, [isAuthenticated, loading, navigate, location.state]);

  // Auth service class
  class AuthService {
    constructor() {
      this.baseURL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';
    }

    async request(endpoint, options = {}) {
      const url = `${this.baseURL}${endpoint}`;
      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      };

      try {
        const response = await fetch(url, config);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Something went wrong');
        }

        return data;
      } catch (error) {
        console.error('API Error:', error);
        throw error;
      }
    }

    async socialLogin(provider, idToken) {
      return this.request(`/auth/social-login/${provider}`, {
        method: 'POST',
        body: JSON.stringify({ accessToken: idToken }),
      });
    }

    async resendVerification(email) {
      return this.request('/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    }
  }

  const authService = new AuthService();

  // Initialize Google OAuth
  useEffect(() => {
    const initializeGoogleAuth = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: import.meta.env.REACT_APP_GOOGLE_CLIENT_ID || "760038216153-0bqu43e35jvl486h6kago349g4s5ip9l.apps.googleusercontent.com",
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true
        });

        // Render the Google Sign-In button
        window.google.accounts.id.renderButton(
          document.getElementById("google-signin-button"),
          { 
            theme: "outline", 
            size: "large",
            text: "signin_with",
            shape: "rectangular",
            width: "100%"
          }
        );
      }
    };

    // Load Google OAuth script
    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleAuth;
      document.head.appendChild(script);
    } else {
      initializeGoogleAuth();
    }

    // Load Facebook SDK
    if (!window.FB) {
      window.fbAsyncInit = function() {
        window.FB.init({
          appId: import.meta.env.REACT_APP_FACEBOOK_APP_ID,
          cookie: true,
          xfbml: true,
          version: 'v18.0'
        });
      };

      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      document.head.appendChild(script);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear general errors
    if (errors.submit) {
      setErrors(prev => ({
        ...prev,
        submit: ''
      }));
    }

    // Reset verification state
    if (needsVerification) {
      setNeedsVerification(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email wajib diisi';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Password wajib diisi';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password minimal 6 karakter';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setErrors({});
    setNeedsVerification(false);
    
    try {
      // Use the context login function instead of direct API call
      const result = await contextLogin(
        formData.email.trim().toLowerCase(),
        formData.password
      );
      
      if (result.success) {
        // console.log('Login successful');
        
        // Check user role for redirect
        const userData = result.user;
        if (userData.role === 'super admin' || userData.role === 'Super Admin') {
          navigate('/admin-dashboard', { replace: true });
        } else {
          const redirectTo = location.state?.from || '/dashboard';
          navigate(redirectTo, { replace: true });
        }
      } else {
        // Handle login failure
        if (result.message?.includes('Email belum diverifikasi')) {
          setNeedsVerification(true);
          setUserEmail(formData.email);
          setErrors({ 
            submit: 'Email belum diverifikasi. Silakan cek email Anda atau klik tombol di bawah untuk mengirim ulang email verifikasi.' 
          });
        } else if (result.message?.includes('Email atau password salah')) {
          setErrors({ 
            submit: 'Email atau password salah. Periksa kembali kredensial Anda.' 
          });
        } else if (result.message?.includes('Akun Anda tidak aktif')) {
          setErrors({ 
            submit: 'Akun Anda tidak aktif. Hubungi customer service untuk bantuan.' 
          });
        } else {
          setErrors({ 
            submit: result.message || 'Terjadi kesalahan saat login. Silakan coba lagi.' 
          });
        }
      }
      
    } catch (error) {
      console.error('Login failed:', error);
      setErrors({ 
        submit: error.message || 'Terjadi kesalahan saat login. Silakan coba lagi.' 
      });
    }
    
    setIsLoading(false);
  };

  const handleResendVerification = async () => {
    if (!userEmail) return;
    
    try {
      setIsLoading(true);
      const response = await authService.resendVerification(userEmail);
      
      if (response.success) {
        setErrors({ 
          submit: 'Email verifikasi berhasil dikirim ulang. Silakan cek email Anda.' 
        });
        setNeedsVerification(false);
      }
    } catch (error) {
      console.error('Resend verification failed:', error);
      setErrors({ 
        submit: error.message || 'Gagal mengirim ulang email verifikasi.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Google Login Handler
  const handleGoogleResponse = async (response) => {
    try {
      setIsLoading(true);
      // console.log('Google response received:', response);
      
      const result = await authService.socialLogin('google', response.credential);
      
      if (result.success) {
        // Store the token and user data manually for social login
        sessionStorage.setItem('auth_token', result.token);
        sessionStorage.setItem('user_data', JSON.stringify(result.data.user));
        
        // Check user role for redirect before reload
        const userData = result.data.user;
        if (userData.role === 'super admin' || userData.role === 'Super Admin') {
          window.location.href = '/admin-dashboard';
        } else {
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Google login failed:', error);
      setErrors({ 
        submit: error.message || 'Gagal login dengan Google. Silakan coba lagi.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Manual Google Login Button Handler
  const handleGoogleLogin = () => {
    if (window.google) {
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // console.log('Google One Tap not displayed or skipped');
        }
      });
    } else {
      setErrors({ 
        submit: 'Google login tidak tersedia. Silakan muat ulang halaman.' 
      });
    }
  };

  // Facebook Login Handler
  const handleFacebookLogin = () => {
    if (!window.FB) {
      setErrors({ 
        submit: 'Facebook login tidak tersedia. Silakan muat ulang halaman.' 
      });
      return;
    }

    window.FB.login(async (response) => {
      if (response.authResponse) {
        try {
          setIsLoading(true);
          const result = await authService.socialLogin('facebook', response.authResponse.accessToken);
          
          if (result.success) {
            // Store the token and user data manually for social login
            sessionStorage.setItem('auth_token', result.token);
            sessionStorage.setItem('user_data', JSON.stringify(result.data.user));
            
            // Check user role for redirect before reload
            const userData = result.data.user;
            if (userData.role === 'super admin' || userData.role === 'Super Admin') {
              window.location.href = '/admin-dashboard';
            } else {
              window.location.reload();
            }
          }
        } catch (error) {
          console.error('Facebook login failed:', error);
          setErrors({ 
            submit: error.message || 'Gagal login dengan Facebook. Silakan coba lagi.' 
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        // console.log('Facebook login cancelled');
      }
    }, { scope: 'email,public_profile' });
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full"></div>
          <p className="text-gray-300">Memverifikasi akses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <style>
        {` 
         .card-gradient {
            background: #ffffffcc;
            border: 1px solid rgba(30, 155, 76, 0.3);
            backdrop-filter: blur(10px);
          }
          
          .input-field {
            background: rgba(255, 255, 255, 0.7);
            border: 1px solid rgba(30, 155, 76, 0.4);
            transition: all 0.3s ease;
          }
          
          .input-field:focus {
            border-color: rgba(30, 155, 76, 0.7);
            box-shadow: 0 0 0 3px rgba(30, 155, 76, 0.2);
            background: #fff;
          }
          
          .btn-gradient {
            background: linear-gradient(135deg, #1E9B4C, #136d35);
          }
          
          .btn-gradient:hover {
            background: linear-gradient(135deg, #136d35, #1E9B4C);
            transform: translateY(-2px);
            box-shadow: 0 10px 25px rgba(30, 155, 76, 0.3);
          }
        `}
      </style>
      
      {/* Hero Section */}
      <section className="hero-gradient py-20"
      style={{
        backgroundImage: "url('https://telnet.net.id/assets/bg.png')",
        backgroundColor:  '#dfe4e0ff',
        backgroundBlendMode: 'overlay',
      }}
      >
        <div className="max-w-md mx-auto px-4">
          <div className="text-center mb-8">
            <img
                src="https://telnet.co.id/wp-content/uploads/2024/09/TELNET-INDONESIA-3.png"
                alt="Icon Image"
                className="w-42 h-32 object-contain mx-auto"
                style={{ maxWidth: '70%' }}
            />
            <h1 className="text-4xl font-bold text-black mb-4">
              Masuk ke Akun
            </h1>
            <p className="text-xl text-black">
              Selamat datang kembali! Silakan masuk untuk melanjutkan
            </p>
          </div>

          {/* Login Form */}
          <div className="card-gradient rounded-3xl p-8 shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Messages */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm">{errors.submit}</p>
                  {needsVerification && (
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      className="mt-2 text-sm text-blue-600 hover:underline"
                      disabled={isLoading}
                    >
                      Kirim ulang email verifikasi
                    </button>
                  )}
                </div>
              )}

              {/* Email Field */}
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input-field w-full px-4 py-3 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none"
                  placeholder="nama@email.com"
                  disabled={isLoading}
                  autoComplete="email"
                />
                {errors.email && (
                  <p className="text-red-600 text-sm">{errors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="input-field w-full px-4 py-3 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none pr-12"
                    placeholder="Masukkan password Anda"
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                 <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600 hover:text-[#1E9B4C] transition-colors"
                  disabled={isLoading}
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
                </div>
                {errors.password && (
                  <p className="text-red-600 text-sm">{errors.password}</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    name="rememberMe"
                    type="checkbox"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-[#1E9B4C] bg-white border-gray-400 rounded focus:ring-[#1E9B4C] focus:ring-2"
                    disabled={isLoading}
                  />
                  <label
                    htmlFor="rememberMe"
                    className="ml-2 text-sm text-gray-700"
                  >
                    Ingat saya
                  </label>
                </div>
                <Link
                  to="/forgot-password"
                  className="text-sm text-[#1E9B4C] hover:underline transition-colors"
                >
                  Lupa password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full px-6 py-3 btn-gradient text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Sedang masuk..." : "Masuk"}
              </button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-400"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-600">atau</span>
                </div>
              </div>

              {/* Social Login */}
              <div className="space-y-3">
                {/* Google Sign-In Button Container */}
                <div id="google-signin-button" className="w-full"></div>

                {/* Facebook */}
                <a
                href={`https://www.facebook.com/v12.0/dialog/oauth?client_id=YOUR_APP_ID&redirect_uri=YOUR_REDIRECT_URI&scope=email,public_profile&response_type=code`}
                className="w-full px-6 py-3 bg-[#1877F2] text-white font-semibold rounded-xl hover:bg-[#166FE5] transition-all duration-300 flex items-center justify-center space-x-2"
                >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span>Masuk dengan Facebook</span>
              </a>
              </div>

              {/* Sign Up Link */}
              <div className="text-center">
                <p className="text-gray-700">
                  Belum punya akun?{" "}
                  <Link
                    to="/register"
                    className="text-[#1E9B4C] hover:underline font-semibold transition-colors"
                  >
                    Daftar sekarang
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LoginPage;